/**
 * Image compression utility for fal.ai upload
 * fal.ai has a 10MB file size limit for images and rejects dimensions above
 * 3850×3850 with a 422 image_too_large. We stay safely below both caps.
 * This compresses images using Canvas API while preserving quality for AI generation.
 */
import { UPLOAD_IMAGE_MIME_TYPES } from '@repo/shared';

const MAX_FILE_SIZE = 9 * 1024 * 1024; // 9MB (safe margin under 10MB limit)
// fal.ai hard-rejects >3850 on either axis, so cap at 3840 with a small safety margin.
const MAX_DIMENSION = 3840;
const INITIAL_QUALITY = 0.92;
const MIN_QUALITY = 0.7;
const QUALITY_STEP = 0.05;

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);

function hasHeicExtension(filename: string): boolean {
  return /\.(heic|heif)$/i.test(filename);
}

function isHeicLikeFile(file: File): boolean {
  return HEIC_MIME_TYPES.has(file.type.toLowerCase()) || hasHeicExtension(file.name);
}

function isNativelyUploadableImage(file: File): boolean {
  return UPLOAD_IMAGE_MIME_TYPES.includes(file.type as typeof UPLOAD_IMAGE_MIME_TYPES[number]);
}

async function convertHeicToJpeg(file: File): Promise<File | null> {
  try {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: INITIAL_QUALITY,
    });

    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob) return null;

    const nextName = hasHeicExtension(file.name)
      ? file.name.replace(/\.(heic|heif)$/i, '.jpg')
      : `${file.name}.jpg`;

    return new File([blob], nextName, { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

/**
 * Probe the image's pixel dimensions without decoding the whole thing into a canvas.
 * Used to decide whether we can skip the resize/compress pass entirely.
 */
async function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Compress image file if it exceeds the fal.ai limits.
 * - Resizes to max 3840×3840 maintaining aspect ratio (fal rejects >3850)
 * - Compresses to JPEG, progressively reducing quality until under 9MB
 * - Converts HEIC/HEIF to JPEG before upload
 * - Returns original file if it's already in a supported format AND under 9MB AND
 *   within the dimension cap
 */
export async function compressImageForUpload(file: File): Promise<File> {
  const hasImageMimeType = file.type.startsWith('image/');
  const heicLikeFile = isHeicLikeFile(file);

  // Skip non-image files
  if (!hasImageMimeType && !heicLikeFile) return file;

  let workingFile = file;

  if (heicLikeFile) {
    const convertedHeic = await convertHeicToJpeg(file);
    if (!convertedHeic) {
      return file;
    }
    workingFile = convertedHeic;
  }

  const needsFormatNormalization = !isNativelyUploadableImage(workingFile);

  // Fast path: file is small, format is supported — but we still need to make
  // sure dimensions fit fal.ai's 3850px cap, otherwise the upload will 422.
  if (!needsFormatNormalization && workingFile.size <= MAX_FILE_SIZE) {
    const dims = await readImageDimensions(workingFile);
    const fitsDimensions = dims && dims.width <= MAX_DIMENSION && dims.height <= MAX_DIMENSION;
    if (fitsDimensions) return workingFile;
    // Oversize in at least one axis — fall through to the resize/encode path.
  }

  return new Promise((resolve, reject) => {
    const img = new window.Image();

    img.onload = () => {
      URL.revokeObjectURL(img.src);

      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      // Progressive compression: reduce quality until under size limit
      const tryCompress = (quality: number) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            if (blob.size <= MAX_FILE_SIZE || quality <= MIN_QUALITY) {
              const ext = 'jpg';
              const newName = workingFile.name.replace(/\.[^.]+$/, `.${ext}`);
              resolve(new File([blob], newName, { type: 'image/jpeg' }));
            } else {
              // Try lower quality
              tryCompress(quality - QUALITY_STEP);
            }
          },
          'image/jpeg',
          quality
        );
      };

      tryCompress(INITIAL_QUALITY);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error(
        heicLikeFile || needsFormatNormalization
          ? 'Не удалось обработать фото с iPhone. Попробуйте сохранить его как JPG/PNG и загрузить снова.'
          : 'Failed to load image for compression'
      ));
    };

    img.src = URL.createObjectURL(workingFile);
  });
}
